'use client';

import React, { useCallback, useEffect, useState } from 'react';
import BaseModal from 'src/components/primitives/BaseModal/BaseModal';
import useWalletsToDisplay from 'src/hooks/useWalletsToDisplay';
import {
	DRIFT_CUSTOM_WALLET_OPTIONS,
	DriftWalletName,
} from 'src/constants/wallets';
import {
	getHistoricallySelectedWallets,
	HISTORICALLY_SELECTED_WALLETS_STORAGE_KEY,
} from 'src/hooks/useWalletsToDisplay';
import useHandleSelectWalletProvider from 'src/hooks/useHandleSelectWalletProvider';
import useHasAcknowledgedTerms from 'src/hooks/useHasAcknowledgedTerms';
import useWalletIsConnected from 'src/hooks/useWalletIsConnected';
import useCurrentWalletConnectionState from 'src/hooks/useCurrentWalletConnectionState';
import useHasConnectedWalletBefore from 'src/hooks/useHasConnectedWalletBefore';
import MagicAuthLoginPage from './MagicAuthLoginPage';
import { useAccountFetchedAndExists } from 'src/hooks/user/useAccountFetchedAndExists';
import { MainWalletsPage } from './MainWalletsPage';
import { MoreWalletsPage } from './MoreWalletsPage';
import useModal from 'src/components/Modals/hooks/useModal';
import PrivyEmailOtpPage from 'src/components/Modals/Onboarding/ConnectWalletModal/PrivyEmailOtpPage/PrivyEmailOtpPage';
import usePrivyEmailOtpResendTimeout from 'src/components/Modals/Onboarding/ConnectWalletModal/PrivyEmailOtpPage/hooks/usePrivyEmailOtpResendTimeout';
import useIsMobileScreenSize from 'src/hooks/useIsMobileScreenSize';
import { cj } from 'src/utils/classNames';

const CONNECTION_STEP_LOCK_KEY = 'ONBOARDING_CONNECTING_LOCK';
const MAX_DEFAULT_WALLETS_TO_SHOW = 2;

export interface ConnectWalletModalProps {
	callback?: () => void;
	startPage?: 'main' | 'magic';
}

export default function ConnectWalletModal({
	callback,
	startPage,
}: ConnectWalletModalProps) {
	const walletConnectionState = useCurrentWalletConnectionState();
	const connected = useWalletIsConnected();
	const isMobileScreenSize = useIsMobileScreenSize();
	const { show, hide, canGoBack, goBack } = useModal();

	const { otpResendSecondsRemaining, setIsOtpSent } =
		usePrivyEmailOtpResendTimeout();

	const [hasAcknowledgedTerms] = useHasAcknowledgedTerms();
	const [hasAccountsLoaded, accountExists] = useAccountFetchedAndExists();

	const [privyEmail, setPrivyEmail] = useState('');

	const { handleSelectProvider } = useHandleSelectWalletProvider();
	const [_, setHasConnectedWalletBefore] = useHasConnectedWalletBefore();

	const {
		walletsToShow,
		hiddenWallets,
		hiddenDetectedWallets,
		hiddenNonDetectedWallets,
	} = useWalletsToDisplay(MAX_DEFAULT_WALLETS_TO_SHOW);

	const [isMagicLinkAuth, setIsMagicLinkAuth] = useState(startPage === 'magic');
	const [isPrivyEmailAuth, setIsPrivyEmailAuth] = useState(false);
	const [showMoreWallets, setShowMoreWallets] = useState(false);

	const handleAddToHistoricallySelectedWallets = useCallback((name: string) => {
		const previouslySelectedWallets = getHistoricallySelectedWallets();
		if (!previouslySelectedWallets.includes(name)) {
			previouslySelectedWallets.push(name);
			localStorage.setItem(
				HISTORICALLY_SELECTED_WALLETS_STORAGE_KEY,
				JSON.stringify(previouslySelectedWallets)
			);
		}
	}, []);

	const handleSelectWallet = useCallback(
		async (walletName: DriftWalletName) => {
			handleAddToHistoricallySelectedWallets(walletName);
			switch (walletName) {
				case DRIFT_CUSTOM_WALLET_OPTIONS.MAGIC_AUTH_WALLET_ADAPTER_NAME:
					setIsMagicLinkAuth(true);
					setIsPrivyEmailAuth(false);
					setShowMoreWallets(false);
					break;
				default: {
					setIsMagicLinkAuth(false);
					setIsPrivyEmailAuth(false);
					walletConnectionState.instance.update(
						'Connecting',
						true,
						CONNECTION_STEP_LOCK_KEY
					);
					await handleSelectProvider(walletName);
				}
			}
		},
		[
			handleAddToHistoricallySelectedWallets,
			handleSelectProvider,
			walletConnectionState.instance,
		]
	);

	const handleLoginWithPrivyEmail = useCallback(() => {
		setIsPrivyEmailAuth(true);
		setIsOtpSent(true);
	}, []);

	const handleClose = useCallback(
		(callCallback = true) => {
			hide();

			if (callback && callCallback) {
				callback();
			}
		},
		[callback, hide]
	);

	const handleBackFromMagicAuth = () => {
		setIsMagicLinkAuth(false);
	};

	const handleBackFromPrivyEmailOtp = () => {
		setIsPrivyEmailAuth(false);
	};

	const handleShowMoreWallets = () => {
		setIsMagicLinkAuth(false);
		setIsPrivyEmailAuth(false);
		setShowMoreWallets(true);
	};

	const handleBackToMainWallets = () => {
		setShowMoreWallets(false);
	};

	// initialize the appkit wallet provider - we dynamically import this so that the appkit wallet provider is only initialized when the modal is opened (reduces initial bundle size)
	useEffect(() => {
		import('src/utils/appKitWalletSetup').then(({ setupAppKitWallet }) =>
			setupAppKitWallet()
		);
	}, []);

	// Once connected, always release onboarding's temporary connecting lock.
	useEffect(() => {
		if (!connected) {
			return;
		}

		walletConnectionState.instance.update(
			'Connecting',
			false,
			CONNECTION_STEP_LOCK_KEY
		);
	}, [connected, walletConnectionState.instance]);

	// close modal if user is connected and has already acknowledged terms
	useEffect(() => {
		if (connected && hasAccountsLoaded) {
			setHasConnectedWalletBefore(true);

			if (canGoBack) {
				goBack();
				return;
			}

			if (
				!hasAcknowledgedTerms.shouldShowTermsReminder &&
				accountExists === false
			) {
				// show deposit modal if account doesn't exist
				show({ type: 'deposit', props: {} });

				return;
			}

			handleClose();
		}
	}, [
		connected,
		hasAcknowledgedTerms.shouldShowTermsReminder,
		hasAccountsLoaded,
		accountExists,
		handleClose,
		setHasConnectedWalletBefore,
		show,
		goBack,
		canGoBack,
		hide,
	]);

	const title = (() => {
		if (isMagicLinkAuth) {
			return 'Magic Link Login';
		}

		if (showMoreWallets) {
			return 'More Wallets';
		}

		return '';
	})();

	const onBack = (() => {
		if (isMagicLinkAuth) {
			return handleBackFromMagicAuth;
		}

		if (isPrivyEmailAuth) {
			return handleBackFromPrivyEmailOtp;
		}

		if (showMoreWallets) {
			return handleBackToMainWallets;
		}

		return undefined;
	})();

	const showBack = showMoreWallets || isMagicLinkAuth || isPrivyEmailAuth;
	const showTitle = isMobileScreenSize || showBack;

	const content = (() => {
		if (isMagicLinkAuth) {
			return (
				<MagicAuthLoginPage onBackFromMagicAuth={handleBackFromMagicAuth} />
			);
		}

		if (isPrivyEmailAuth) {
			return (
				<PrivyEmailOtpPage
					email={privyEmail}
					setIsOtpSent={setIsOtpSent}
					resendSecondsRemaining={otpResendSecondsRemaining}
				/>
			);
		}

		if (showMoreWallets) {
			return (
				<MoreWalletsPage
					detectedWallets={hiddenDetectedWallets}
					nonDetectedWallets={hiddenNonDetectedWallets}
					onChooseWallet={handleSelectWallet}
					onBack={handleBackToMainWallets}
				/>
			);
		}

		return (
			<MainWalletsPage
				privyEmail={privyEmail}
				setPrivyEmail={setPrivyEmail}
				wallets={walletsToShow}
				hiddenWallets={hiddenWallets}
				onChooseWallet={handleSelectWallet}
				onShowMoreWallets={handleShowMoreWallets}
				onLoginWithPrivyEmail={handleLoginWithPrivyEmail}
				privyOtpResendSecondsRemaining={otpResendSecondsRemaining}
			/>
		);
	})();

	return (
		<BaseModal.Container
			className="sm:max-h-135"
			onClose={() => handleClose(false)}
			dataTestId="connect-wallet-modal"
		>
			<BaseModal.Title
				className={cj(!showTitle && 'sr-only')}
				onClose={() => handleClose(false)}
				showBack={showBack}
				onBack={onBack}
				closeButtonTestId="connect-wallet-modal-close"
			>
				{title}
			</BaseModal.Title>
			<BaseModal.Body>{content}</BaseModal.Body>
		</BaseModal.Container>
	);
}
